import { useEffect, useRef, useState } from "react";
import { getLenis } from "@/lib/scroll";

/**
 * The mark as a reading-progress dial: the rim fills as the page scrolls, and
 * a click returns to the top. Blended with the page so it stays legible over
 * both dark and light sections.
 */
export function ScrollMark() {
  const rim = useRef<SVGCircleElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const progress = max > 0 ? Math.min(1, window.scrollY / max) : 0;
      if (rim.current) rim.current.style.strokeDashoffset = String(1 - progress);
      setVisible(window.scrollY > 600);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    schedule();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, []);

  const toTop = () => {
    const lenis = getLenis();
    if (lenis) lenis.scrollTo(0, { duration: 1.6 });
    else window.scrollTo({ top: 0 });
  };

  return (
    <button
      type="button"
      aria-label="Back to top"
      tabIndex={visible ? 0 : -1}
      onClick={toTop}
      className={`group fixed bottom-5 right-5 z-40 h-12 w-12 text-white mix-blend-difference transition-[opacity,transform] duration-500 ease-out-expo sm:bottom-7 sm:right-7 ${
        visible ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-3 opacity-0"
      }`}
    >
      <svg viewBox="0 0 32 32" fill="none" aria-hidden="true" className="h-full w-full">
        <circle cx="16" cy="16" r="13.2" stroke="currentColor" strokeWidth="1.4" opacity="0.28" />
        <circle
          ref={rim}
          cx="16"
          cy="16"
          r="13.2"
          stroke="currentColor"
          strokeWidth="1.4"
          pathLength={1}
          strokeDasharray="1"
          strokeDashoffset="1"
          transform="rotate(-90 16 16)"
        />
        <g className="transition-transform duration-500 ease-out-expo group-hover:-translate-x-[0.8px] group-hover:-translate-y-[0.6px]">
          <ellipse cx="16.8" cy="16.6" rx="8" ry="7.4" stroke="currentColor" strokeWidth="1.4" opacity="0.6" />
          <circle cx="17.6" cy="17.2" r="3" fill="currentColor" />
        </g>
      </svg>
    </button>
  );
}
